// @ts-check

// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const Icon = require('client-v2/common/containers/icons/component')
const { useModalDepthOverlay } = require('client-v2/common/contexts/modal-depth')
const useClientEffect = require('client-v2/common/hooks/client-effect')
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('Modal')

/**
 * @param {Object} props
 * @param {boolean} props.dismissOnBackdropClick
 * @param {boolean} props.hasBackdrop
 * @param {boolean} props.hasCloseButton
 * @param {React.ElementType} props.children
 * @param {string} props.ariaLabel
 * @param {string} props.className
 * @param {React.ReactElement<any>} props.wrapperElement
 * @param {(event: Event) => void} props.onDismiss
 */
function Modal({
  // Flags
  dismissOnBackdropClick,
  hasBackdrop,
  hasCloseButton,
  // Values
  ariaLabel,
  children,
  className,
  wrapperElement = tag('div'),
  // Callbacks
  onDismiss
}) {
  const contentRef = React.useRef()
  useModalDepthOverlay(contentRef)

  useClientEffect(
    () => {
      const rootLinks = document.querySelectorAll('#root a, #root button')
      rootLinks.forEach(link => {
        link.setAttribute('tabindex', '-1')
      })
      return () => {
        rootLinks.forEach(link => {
          link.setAttribute('tabindex', '0')
        })
      }
    },
    []
  )

  return (
    React.cloneElement(wrapperElement, {
      className: classNames(
        wrapperElement.props.className,
        className,
        bem.block()
      ),
      // Accessibility requirements
      'role': 'dialog',
      'aria-label': ariaLabel
    },
      !!hasBackdrop && (
        tag('div', {
          className: bem.element('backdrop'),
          onClick: dismissOnBackdropClick ? onDismiss : null
        })
      ),
      tag('div', {
        ref: contentRef,
        className: bem.element('content')
      },
        children
      ),
      !!(hasCloseButton && onDismiss) && (
        tag(CircleButton, {
          'aria-label': 'Close dialog',
          className: bem.element('close-button'),
          onClick: onDismiss
        },
          tag(Icon, { id: 'x' })
        )
      )
    )
  )
}

Modal.defaultProps = {
  dismissOnBackdropClick: true,
  hasBackdrop: true,
  hasCloseButton: true
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  Modal.propTypes = {
    // Flags
    dismissOnBackdropClick: PropTypes.bool,
    hasBackdrop: PropTypes.bool,
    hasCloseButton: PropTypes.bool,
    // Values
    ariaLabel: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    wrapperElement: PropTypes.element,
    // Callbacks
    onDismiss: PropTypes.func
  }
}

module.exports = Modal
